import { borderRadius, color, css, LoaderSpinner, spacing, styled } from '@lessonup/ui-components';
import React, { ReactNode } from 'react';

type ButtonType = 'secondary';

type ButtonPadding = 'small';
type Shadow = 'small';
type Radius = 'small';

type PinButtonThemeSettings = {
  colors: Record<
    ButtonType | 'disabled' | 'selected',
    {
      fill: string;
      on: string;
      stroke: string;
      fillFeedback: string;
    }
  >;
  padding: Record<ButtonPadding, string>;
  shadow: Record<Shadow, string>;
  radius: Record<Radius, string>;
};

export interface PinButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  label: ReactNode;
  buttonType: ButtonType;
  padding?: ButtonPadding;
  shadow?: Shadow;
  radius?: Radius;
  selected?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  as?: React.ElementType;
}

/**
 * Button component used inside PinComponents.
 *
 * These buttons follow their own styling system separate from the main LessonUp UI,
 * adapting their appearance based on the teacher's chosen lesson theme. See ED-1414
 *
 * Note: Only the properties necessary for the EmojiPoll answers are implemented at the moment.
 */
export const PinButton: React.FC<PinButtonProps> = ({
  as: T = 'button',
  buttonType,
  padding = 'small',
  shadow = 'small',
  radius = 'small',
  disabled = false,
  onClick,
  selected = false,
  loading = false,
  label,
  ...props
}) => {
  const theme: PinButtonThemeSettings = {
    colors: {
      secondary: {
        fill: color.neutralNew.surface.fill,
        on: color.neutralNew.surface.on,
        stroke: color.neutralNew.surface.stroke,
        fillFeedback: color.neutralNew.surface.fillFeedback,
      },
      disabled: {
        fill: color.state.disabled.fill,
        on: color.state.disabled.on,
        stroke: color.state.disabled.stroke,
        fillFeedback: color.state.disabled.fillFeedback,
      },
      selected: {
        fill: color.state.active.on,
        on: color.state.active.fill,
        stroke: color.state.active.on,
        fillFeedback: color.state.active.fill,
      },
    },
    padding: {
      small: spacing.size8,
    },
    shadow: {
      small: `0 1px 0 rgba(0, 0, 0, 0.10)`,
    },
    radius: {
      small: borderRadius.rounded4,
    },
  };

  return (
    <StyledPinButton
      as={T}
      buttonType={buttonType}
      padding={padding}
      onClick={onClick}
      disabled={disabled}
      $selected={selected}
      shadow={shadow}
      radius={radius}
      theme={theme}
      $loading={loading}
      {...props}
    >
      <ContentWrapper>
        {loading && (
          <LoaderWrapper $loading={loading}>
            <LoaderSpinner />
          </LoaderWrapper>
        )}
        {label}
      </ContentWrapper>
    </StyledPinButton>
  );
};

const StyledPinButton = styled.button<
  Pick<PinButtonProps, 'onClick'> &
    Required<Pick<PinButtonProps, 'buttonType' | 'padding' | 'shadow' | 'radius' | 'disabled'>> & {
      $loading: boolean;
      $selected: boolean;
      theme: PinButtonThemeSettings;
      children: ReactNode;
    }
>`
  ${({ theme, radius }) => css`
    border-radius: ${theme.radius[radius]};
  `}
  ${({ theme, shadow }) => css`
    box-shadow: ${theme.shadow[shadow]};
  `}
  ${({ theme, padding }) => css`
    padding: ${theme.padding[padding]};
  `}
  ${({ theme, buttonType, disabled, $selected, onClick }) => {
    const colorset = disabled ? 'disabled' : $selected ? 'selected' : buttonType;
    return css`
      background-color: ${theme.colors[colorset].fill};
      color: ${theme.colors[colorset].on};
      border: 1px solid ${theme.colors[colorset].stroke};

      ${onClick &&
      !disabled &&
      !$selected &&
      css`
        &:hover {
          background-color: ${theme.colors[colorset].fillFeedback};
        }
      `}
    `;
  }}
  ${({ onClick, disabled }) =>
    onClick && !disabled
      ? css`
          cursor: pointer;
        `
      : css`
          cursor: default;
        `}

  ${({ $loading }) =>
    $loading &&
    css`
      & > * {
        visibility: hidden;
      }
    `}
`;

const ContentWrapper = styled.div`
  position: relative;
`;

const LoaderWrapper = styled.div<{ $loading: boolean }>`
  ${({ $loading }) =>
    $loading &&
    css`
      cursor: default;
      :not(svg) {
        visibility: initial;
      }
    `}
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
`;
